/**
 * useThemeMode — Wave 13a (#XXX).
 *
 * Studio-side light/dark mode toggle for web.  iOS stays dark-
 * forever (see sibling wave killing iOS themes); this only
 * affects the web app.
 *
 * Persists to `orbit_theme_mode` in localStorage — string values
 * `'light'` or `'dark'`.  Applies the choice at three levels so
 * every layer of the stack stays in sync:
 *   1. `document.documentElement.dataset.theme` — the primary
 *      source of truth read by the `[data-theme="…"]` blocks in
 *      `src/index.css`.
 *   2. `.dark` class on `<html>` — kept in sync for any legacy
 *      consumer that reads it (next-themes, tailwind `dark:`
 *      variants, etc.).
 *   3. `.new-shell` class on `<html>` — added ONLY in dark mode
 *      so the pure-black ambient body backdrop and the glass
 *      surface treatment don't paint over the light canvas.
 *
 * Cross-tab sync: writes fire a synthetic `storage` event so
 * every subscriber in the current tab re-renders alongside the
 * native cross-tab `storage` event.
 *
 * Bootstrap happens BEFORE React mounts (see `src/main.tsx`) to
 * avoid a light→dark flash on first paint.
 */
import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

/** localStorage key — matches the Wave 13a spec. */
export const THEME_MODE_KEY = 'orbit_theme_mode';

/** darkScope class string — apply to any legacy wrapper whose
 *  children still surface the light Mission Control palette
 *  (bg-card, text-foreground, etc.) so they read on the dark
 *  cosmic canvas.  In light mode this is a no-op; call
 *  `useDarkScopeClass()` for the auto-gated version.
 *
 *  POL-11b — bumped scoped translucent bumps one notch so cards on
 *  Today read cleanly against the starfield instead of the sky
 *  bleeding through and killing legibility.  bg-card / bg-white:
 *  `!bg-white/[0.04]` → `!bg-white/[0.06]`; bg-muted:
 *  `!bg-white/[0.06]` → `!bg-white/[0.08]`.  Kept the same
 *  "sitting on the sky" aesthetic — cards remain translucent, just
 *  enough opacity that text has contrast. */
export const DARK_SCOPE_CLASS =
  '[&_.bg-card]:!bg-white/[0.06] [&_.bg-white]:!bg-white/[0.06] ' +
  '[&_.bg-background]:!bg-transparent [&_.bg-muted]:!bg-white/[0.08] ' +
  '[&_.border-border]:!border-white/[0.10] ' +
  '[&_.text-foreground]:!text-white [&_.text-muted-foreground]:!text-white/55 ' +
  '[&_.shadow-sm]:!shadow-none [&_.shadow-md]:!shadow-none [&_.shadow-lg]:!shadow-none';

/**
 * Read the stored mode synchronously so the first render picks
 * the right value (no flash).  Falls back to `'dark'` — the
 * current app default.
 */
function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(THEME_MODE_KEY);
    return stored === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/**
 * Apply the mode to <html> — sets data-theme, toggles the .dark
 * class, and toggles .new-shell (which owns the cosmic body
 * backdrop and only makes sense in dark mode).
 *
 * Exported so `main.tsx` can call it BEFORE the React tree
 * mounts, avoiding a paint flash.
 */
export function applyThemeMode(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.dataset.theme = mode;
  html.classList.toggle('dark', mode === 'dark');
  html.classList.toggle('new-shell', mode === 'dark');
}

export function useThemeMode(): {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
} {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);

  // Cross-tab + cross-subscriber sync — pick up mode changes
  // fired from other tabs (native `storage` event) OR from other
  // useThemeMode subscribers in the current tab (synthetic
  // dispatch in setMode below).
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== THEME_MODE_KEY) return;
      const next: ThemeMode = e.newValue === 'light' ? 'light' : 'dark';
      setModeState(next);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Reapply to <html> whenever mode changes — covers the case
  // where the pre-render bootstrap in main.tsx didn't run (e.g.
  // during hot-reload).
  useEffect(() => {
    applyThemeMode(mode);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    try {
      window.localStorage.setItem(THEME_MODE_KEY, next);
      // Same-tab notification so any other useThemeMode() consumer
      // re-renders without needing a prop / context.  The native
      // `storage` event only fires across tabs.
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: THEME_MODE_KEY,
          newValue: next,
        }),
      );
    } catch {
      // Private mode / quota — the DOM update below still wins
      // for the current session.
    }
    setModeState(next);
    // Apply immediately (no need to wait for the effect) so the
    // click-to-repaint is truly instant.
    applyThemeMode(next);
  }, []);

  return { mode, setMode };
}

/**
 * Convenience — returns DARK_SCOPE_CLASS only when the studio is
 * in dark mode.  Every surface that previously hard-coded the
 * darkScope override string should route through this so light
 * mode gets a clean pass-through.
 */
export function useDarkScopeClass(): string {
  const { mode } = useThemeMode();
  return mode === 'dark' ? DARK_SCOPE_CLASS : '';
}
